// contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true); // Default to true to avoid false warnings

  useEffect(() => {
    let mounted = true;

    // Check for OAuth redirect and initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          return;
        }

        if (mounted) {
          setUser(session?.user ?? null);
          
          // Check email verification status for email/password users
          if (session?.user) {
            // OAuth providers automatically verify emails, so only check for email signup
            const isPasswordAuth = session.user.app_metadata.provider === 'email';
            const isVerified = !isPasswordAuth || session.user.email_confirmed_at != null;
            setEmailVerified(isVerified);
          }
          
          // Only redirect on initial OAuth completion and if we're not already on the profile page
          const isOAuthRedirect = session?.user?.app_metadata?.provider && 
                                window.location.hash.includes('access_token');
          
          if (isOAuthRedirect && window.location.pathname !== '/profile') {
            console.log('OAuth redirect detected, navigating to profile');
            window.location.href = '/profile';
            return;
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        if (mounted) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event, session?.user?.id);
      
      if (event === 'SIGNED_IN') {
        if (mounted) {
          setUser(session?.user ?? null);
          
          // Check email verification status
          let isVerified = true;
          if (session?.user) {
            const isPasswordAuth = session.user.app_metadata.provider === 'email';
            isVerified = !isPasswordAuth || session.user.email_confirmed_at != null;
            setEmailVerified(isVerified);
          }
          
          // Check if this is a new sign-in or just a session recovery
          const isNewSignIn = !initialized;
          
          // Only redirect if this is a new sign-in AND email is verified AND we're on the homepage
          if (window.location.pathname === '/' && isNewSignIn && isVerified) {
            console.log('New sign in detected, navigating to profile');
            window.location.href = '/profile';
          }
        }
      } else if (event === 'SIGNED_OUT') {
        if (mounted) {
          setUser(null);
          setEmailVerified(true); // Reset to default
          window.location.href = '/';
        }
      } else if (event === 'USER_UPDATED') {
        if (mounted) {
          setUser(session?.user ?? null);
          
          // Check if email verification status changed
          if (session?.user) {
            const isPasswordAuth = session.user.app_metadata.provider === 'email';
            const isVerified = !isPasswordAuth || session.user.email_confirmed_at != null;
            setEmailVerified(isVerified);
          }
        }
      }
    });

    initializeAuth();

    // Cleanup
    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []); // Note: removed 'initialized' from dependency array as per original code

  const signUp = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
      
      // Set verification status for new sign-ups
      if (data?.user) {
        setEmailVerified(false); // New sign-ups need verification
        
        // Prevent automatic redirect for new sign-ups that need verification
        if (data.user.identities && data.user.identities.length > 0) {
          // This is a new signup that requires email verification
          // We'll return a special flag to indicate this
          return { 
            data, 
            error: null,
            isNewSignUp: true 
          };
        }
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      // Check specifically for the "User already registered" error
      if (error.message && error.message.includes('User already registered')) {
        return { 
          data: null, 
          error: {
            ...error,
            message: 'This email is already registered. Please sign in instead or use a different email.'
          } 
        };
      }
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      
      // Check verification status
      if (data?.user) {
        const isVerified = data.user.email_confirmed_at != null;
        setEmailVerified(isVerified);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      // Just perform a regular sign out without trying global scope
      const { error } = await supabase.auth.signOut();
      
      if (error) throw error;

      // Clear any local state
      setUser(null);
      // setEmailVerified(true); // Handled by onAuthStateChange SIGNED_OUT event
      
      // Clear any stored auth data
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-' + supabase.supabaseUrl + '-auth-token');
      
      // Force navigation to home page
      window.location.href = '/';
      
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if there's an error, try to clean up local state
      setUser(null);
      // setEmailVerified(true); // Handled by onAuthStateChange SIGNED_OUT event
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('sb-' + supabase.supabaseUrl + '-auth-token');
      window.location.href = '/';
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/profile`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
      if (error) throw error;
      // Verification status for OAuth is handled by initializeAuth/onAuthStateChange
      return { data, error: null };
    } catch (error) {
      console.error('Google sign in error:', error);
      return { data: null, error };
    }
  };

  const signInWithApple = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/profile`
        }
      });
      if (error) throw error;
      // Verification status for OAuth is handled by initializeAuth/onAuthStateChange
      return { data, error: null };
    } catch (error) {
      console.error('Apple sign in error:', error);
      return { data: null, error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://nutrinexus.app/reset-password'
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      return { error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { error };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      initialized,
      emailVerified, // Add this to the context value
      signUp,
      signIn,
      signOut,
      signInWithGoogle,
      signInWithApple, // Include original function
      resetPassword,
      updatePassword,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};